import React, { useState, useEffect } from 'react';
import config from '../config.js';

const ICON_BY_EXT = {
  dxf: '📐',
  dwg: '📐',
  pdf: '📄',
  step: '🧱',
  stp: '🧱',
  igs: '🧱',
  iges: '🧱',
  sldprt: '🧊',
  sldasm: '🧊',
  slddrw: '🧊',
};

function formatBytes(b) {
  if (b == null) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

function iconFor(filename) {
  const ext = (filename || '').split('.').pop()?.toLowerCase();
  return ICON_BY_EXT[ext] || '📎';
}

/**
 * Surfaces customer-supplied drawings on a Quote that originated from a
 * boyd-portal submission. Renders only when `customerSubmissionId` is set
 * (i.e. quote.customer_submission_id is non-null). Lists each uploaded
 * file grouped by part, with a single-file download (presigned GET) and
 * a Download All button (zip bundle).
 */
export default function CustomerSubmissionDrawingsCard({
  customerSubmissionId,
}) {
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyFileId, setBusyFileId] = useState(null);
  const [busyAll, setBusyAll] = useState(false);

  const apiBase = config.API_BASE_URL;

  useEffect(() => {
    if (!customerSubmissionId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    // We need the reference_number to fetch detail; the back-link from
    // quote → customer_submission_id is the UUID. The detail endpoint
    // takes the reference_number, so we resolve via list filter or fetch
    // the list for the customer and find the matching id. Simpler:
    // we can hit the new endpoint we'll build OR the existing detail
    // endpoint by ref. For now, find via list scoped to the submission's
    // own ID using the detail-by-ID endpoint when we have the customer.
    // The api gives us /submissions/{reference_number}, but our prop is
    // an id. Workaround: fetch the by-id helper that we expose…
    // Simpler still — we do a direct GET on the row's id via a new
    // helper endpoint? No, we already have that path: detail endpoint
    // accepts the reference_number. So we look up via the existing
    // /customers/37/submissions list endpoint and match on id. That's
    // chatty though. Cleanest: hit the list endpoint and find the row.
    // Since the customer is fixed (Boyd, customer_id=37) on the portal
    // side, this is fine.
    (async () => {
      try {
        // The fastest path is fetching the customer-scoped list and
        // matching by id; in practice the list is small (low tens).
        const listResp = await fetch(`${apiBase}/customers/37/submissions`);
        if (!listResp.ok) throw new Error(`HTTP ${listResp.status}`);
        const list = await listResp.json();
        const match = (list.submissions || []).find(
          (s) => s.id === customerSubmissionId,
        );
        if (!match) {
          if (!cancelled) setError('Originating submission not found.');
          return;
        }
        const detailResp = await fetch(
          `${apiBase}/submissions/${match.reference_number}`,
        );
        if (!detailResp.ok) throw new Error(`HTTP ${detailResp.status}`);
        const detail = await detailResp.json();
        if (!cancelled) setSubmission(detail);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load drawings.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [customerSubmissionId, apiBase]);

  // Render-time computations after hooks (Amplify CRA rule: no early-return
  // before all hooks have run).
  if (!customerSubmissionId) return null;

  if (loading) {
    return (
      <div className="card mb-3 border-info">
        <div className="card-body py-2">
          <span className="spinner-border spinner-border-sm me-2" />
          Loading customer-supplied drawings…
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="card mb-3 border-danger">
        <div className="card-body py-2 text-danger small">
          Couldn't load customer-supplied drawings: {error}
        </div>
      </div>
    );
  }
  if (!submission) return null;

  const allFiles = (submission.parts || []).flatMap((p) =>
    (p.files || []).filter((f) => !!f.uploaded_at).map((f) => ({
      ...f, product_id: p.product_id, part_description: p.description,
    })),
  );

  const handleDownloadOne = async (file) => {
    setBusyFileId(file.id);
    try {
      const resp = await fetch(
        `${apiBase}/submissions/${submission.id}/files/${file.id}/download-url`,
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      // Open the presigned URL — the endpoint sets Content-Disposition
      // attachment so browser downloads with the original filename.
      window.location.href = data.download_url;
    } catch (e) {
      alert(`Download failed: ${e.message || e}`);
    } finally {
      setBusyFileId(null);
    }
  };

  const handleDownloadAll = async () => {
    setBusyAll(true);
    try {
      // Fetch the zip via the auth-aware fetch wrapper, then trigger a
      // browser download via blob URL — direct window.location would
      // skip the X-API-Key header.
      const resp = await fetch(
        `${apiBase}/submissions/${submission.id}/files.zip`,
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${submission.reference_number}_drawings.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      alert(`Download All failed: ${e.message || e}`);
    } finally {
      setBusyAll(false);
    }
  };

  return (
    <div className="card mb-3 border-info">
      <div className="card-header bg-info-subtle d-flex justify-content-between align-items-center">
        <div>
          <strong>📁 Customer-supplied drawings</strong>
          <span className="text-muted small ms-2">
            from {submission.reference_number}
            {submission.type && (
              <> · marked as{' '}
                <span className={`badge ${submission.type === 'job' ? 'bg-primary' : 'bg-warning text-dark'}`}>
                  {submission.type === 'job' ? 'Job (repeat)' : 'Quote (new pricing)'}
                </span>
              </>
            )}
            {submission.submitter_email && (
              <> · {submission.submitter_email}</>
            )}
          </span>
        </div>
        {allFiles.length > 1 && (
          <button
            className="btn btn-sm btn-info"
            disabled={busyAll}
            onClick={handleDownloadAll}
          >
            {busyAll ? (
              <><span className="spinner-border spinner-border-sm me-1" />Building zip…</>
            ) : (
              <>⬇ Download All ({allFiles.length})</>
            )}
          </button>
        )}
      </div>
      <div className="card-body p-2">
        {allFiles.length === 0 ? (
          <div className="text-muted small">
            Customer hasn't uploaded any drawings yet.
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm table-hover mb-0 small">
              <thead>
                <tr>
                  <th style={{ width: '1%' }}></th>
                  <th>File</th>
                  <th>For Part</th>
                  <th className="text-end" style={{ width: '90px' }}>Size</th>
                  <th style={{ width: '110px' }}></th>
                </tr>
              </thead>
              <tbody>
                {allFiles.map((f) => (
                  <tr key={f.id}>
                    <td>{iconFor(f.filename)}</td>
                    <td className="font-monospace text-break">{f.filename}</td>
                    <td>
                      <strong>{f.product_id}</strong>
                      <span className="text-muted"> · {f.part_description}</span>
                    </td>
                    <td className="text-end font-monospace">{formatBytes(f.size_bytes)}</td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-info"
                        disabled={busyFileId === f.id}
                        onClick={() => handleDownloadOne(f)}
                      >
                        {busyFileId === f.id ? '…' : 'Download'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
